import React, { useState, useEffect } from 'react';
import Card from '../../components/common/Card/Card';
import Table from '../../components/common/Table/Table';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import { getMyStudentProjects } from '../../services/studentService';
import { getBOMByProject } from '../../services/bomService';
import styles from './StudentBOM.module.css';
import { sortProjectsByName } from '../../utils/sortProjects';

const StudentBOM = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [bomItems, setBomItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBOM, setLoadingBOM] = useState(false);

  useEffect(() => {
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await getMyStudentProjects();
      setProjects(data);
      if (data.length > 0) {
        setSelectedProject(data[0].id);
        await fetchBOMForProject(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBOMForProject = async (projectId) => {
    setLoadingBOM(true);
    try {
      const data = await getBOMByProject(projectId);
      setBomItems(data);
    } catch (error) {
      console.error('Error fetching BOM:', error);
      setBomItems([]);
    } finally {
      setLoadingBOM(false);
    }
  };

  const handleProjectChange = (e) => {
    const projectId = e.target.value;
    setSelectedProject(projectId);
    fetchBOMForProject(projectId);
  };

  const columns = [
    {
      header: 'Item Name',
      accessor: 'item_name',
      cell: (row) => <span className={styles.itemName}>{row.item_name}</span>
    },
    {
      header: 'Category',
      accessor: 'category',
      cell: (row) => (
        <span className={styles.category}>
          {row.category?.replace(/_/g, ' ').toUpperCase()}
        </span>
      )
    },
    {
      header: 'Quantity',
      accessor: 'quantity',
      cell: (row) => <span>{row.quantity}</span>
    },
    {
      header: 'Unit Price',
      accessor: 'unit_price',
      cell: (row) => <span>${parseFloat(row.unit_price || 0).toFixed(2)}</span>
    },
    {
      header: 'Total Price',
      accessor: 'total_price',
      cell: (row) => {
        const total = (row.quantity || 0) * (row.unit_price || 0);
        return <span className={styles.totalPrice}>${total.toFixed(2)}</span>;
      }
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => (
        <span className={`${styles.status} ${styles[row.status?.toLowerCase() || 'pending']}`}>
          {row.status || 'Pending'}
        </span>
      )
    },
    {
      header: 'Supplier',
      accessor: 'supplier',
      cell: (row) => <span className={styles.supplier}>{row.supplier || '-'}</span>
    }
  ];

  if (loading) {
    return (
      <div className={styles.loading}>
        <LoadingSpinner size="large" message="Loading projects..." />
      </div>
    );
  }

  const totalBudget = bomItems.reduce((sum, item) => {
    return sum + ((item.quantity || 0) * (item.unit_price || 0));
  }, 0);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Bill of Materials</h1>
        {projects.length > 0 && (
          <select
            value={selectedProject || ''}
            onChange={handleProjectChange}
            className={styles.projectSelect}
          >
            {sortProjectsByName(projects).map(project => (
              <option key={project.id} value={project.id}>
                {project.project_name}
              </option>
            ))}
          </select>
        )}
      </div>

      {projects.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <p>No projects assigned yet</p>
          </div>
        </Card>
      ) : (
        <>
          <div className={styles.budgetCard}>
            <Card>
              <div className={styles.budgetInfo}>
                <div className={styles.budgetItem}>
                  <span className={styles.budgetLabel}>Total Items</span>
                  <span className={styles.budgetValue}>{bomItems.length}</span>
                </div>
                <div className={styles.budgetItem}>
                  <span className={styles.budgetLabel}>Total Budget</span>
                  <span className={styles.budgetValue}>${totalBudget.toFixed(2)}</span>
                </div>
              </div>
            </Card>
          </div>

          <Card>
            {loadingBOM ? (
              <div className={styles.loadingBOM}>
                <LoadingSpinner message="Loading BOM items..." />
              </div>
            ) : bomItems.length === 0 ? (
              <div className={styles.empty}>
                <p>No BOM items for this project yet</p>
              </div>
            ) : (
              <Table columns={columns} data={bomItems} />
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default StudentBOM;

